import type { DocumentRowDto, DocumentStatusDto } from '@glory/contracts';
import { t } from '@glory/i18n';
import {
  Button,
  Callout,
  cn,
  DateRangeInput,
  Dialog,
  EmptyState,
  InfoHint,
  PageHeader,
  Segments,
  Select,
  TextInput,
} from '@glory/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FileText, Search, Upload, X } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import { useEffect, useState } from 'react';

import {
  type DocumentsSearch,
  hasFilters,
  parseDocumentsSearch,
} from '../../../features/documents/model/filters.ts';
import { kindOptions, statusLabel } from '../../../features/documents/model/labels.ts';
import { useDocumentList } from '../../../features/documents/runtime/use-documents.ts';
import { DocumentPanel } from '../../../features/documents/ui/document-panel.tsx';
import { DocumentTable } from '../../../features/documents/ui/document-table.tsx';
import { type PresetBinding, UploadDialog } from '../../../features/documents/ui/upload-dialog.tsx';
import { requirePermissionRoute, usePermissions } from '../../../lib/permissions.ts';
import { useDebouncedValue } from '../../../lib/use-debounced-value.ts';

export const Route = createFileRoute('/_app/documents/')({
  validateSearch: (search: Record<string, unknown>) => parseDocumentsSearch(search),
  beforeLoad: requirePermissionRoute('documents.read'),
  component: DocumentsPage,
});

/** Статусы в фильтре — по частоте: подписанные, ждущие подписи, черновики, отозванные. */
const STATUS_FILTERS: readonly (DocumentStatusDto | undefined)[] = [
  undefined,
  'signed',
  'frozen',
  'draft',
  'void',
];

type Binding = 'client' | 'deal' | 'employee';

/** Название привязки из адреса — по строкам таблицы: у документов клиента в строках есть его имя. */
function bindingName(items: readonly DocumentRowDto[], binding: Binding, id: string): string | null {
  for (const row of items) {
    const ref = row[binding];
    if (ref?.id === id) return ref.name;
  }
  return null;
}

/**
 * Хранилище «Документы»: таблица (документ, сторона, сумма, статус, дата), фильтры в адресе — вид, статус,
 * период, поиск по номеру и названию, архив, привязка к клиенту, сделке или сотруднику; карточка — панелью
 * справа; «Загрузить документ».
 */
function DocumentsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const canManage = usePermissions().can('documents.manage');
  const [typed, setTyped] = useState(search.q ?? '');
  const query = useDebouncedValue(typed.trim(), 250);

  // Поиск живёт в адресе: набранное попадает в ссылку, когда ввод затих.
  useEffect(() => {
    if ((search.q ?? '') === query) return;
    void navigate({ search: (prev) => ({ ...prev, q: query === '' ? undefined : query }), replace: true });
  }, [query, search.q, navigate]);

  const list = useDocumentList(search, query);
  const items = list.data?.items ?? [];
  const openIndex = items.findIndex((doc) => doc.id === search.doc);
  // Сужено фильтрами (кроме переключателя «Архив»): пустой список предлагает их сбросить.
  const narrowed = hasFilters({ ...search, archived: undefined, q: query === '' ? undefined : query });

  /** Открыть карточку — новая запись истории (назад закроет её); листать и закрыть — без записи. */
  const show = (id: string | undefined, replace: boolean) => {
    void navigate({ search: (prev) => ({ ...prev, doc: id }), replace });
  };
  const setFilter = (patch: Partial<DocumentsSearch>) => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  };
  const resetFilters = () => {
    setTyped('');
    void navigate({ search: (prev) => ({ doc: prev.doc }), replace: true });
  };
  const openUpload = () => {
    void navigate({ search: (prev) => ({ ...prev, upload: true }) });
  };
  const uploadButton = canManage ? (
    <Button variant="primary" onClick={openUpload}>
      <Upload /> {t('documents.uploadButton')}
    </Button>
  ) : null;

  const bindings = (['deal', 'client', 'employee'] as const).flatMap((binding) => {
    const id = search[binding];
    if (id === undefined) return [];
    const name = bindingName(items, binding, id);
    return [
      {
        binding,
        label:
          name === null ? t(`documents.filters.${binding}Any`) : t(`documents.filters.${binding}`, { name }),
        name,
        id,
      },
    ];
  });
  // Загрузка из отфильтрованного по объекту списка сразу привязывает документ к нему.
  const preset: PresetBinding = Object.fromEntries(
    bindings.flatMap((own) => (own.name === null ? [] : [[own.binding, { id: own.id, name: own.name }]])),
  );

  return (
    <div className="relative flex min-h-full w-full flex-col gap-4 px-4 py-5 md:px-6">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            {t('documents.title')}
            <InfoHint
              hint={{
                title: t('documents.hint.title'),
                text: t('documents.hint.text'),
                example: t('documents.hint.example'),
              }}
            />
          </span>
        }
        actions={uploadButton}
      />
      <p className="-mt-2 max-w-[70ch] text-muted">{t('documents.lead')}</p>

      <div className="flex flex-wrap items-center gap-2">
        <Segments
          size="sm"
          value={search.archived ? 'archived' : 'active'}
          options={[
            { value: 'active', label: t('documents.filters.active') },
            { value: 'archived', label: t('documents.filters.archived') },
          ]}
          onValueChange={(value) => {
            setFilter({ archived: value === 'archived' ? true : undefined });
          }}
        />
        <Select
          size="sm"
          aria-label={t('documents.columns.document')}
          className="w-44"
          placeholder={t('documents.filters.kindAll')}
          clearable
          value={search.kind ?? null}
          options={kindOptions()}
          onValueChange={(kind) => {
            setFilter({ kind: kind ?? undefined });
          }}
        />
        <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('documents.columns.status')}>
          {STATUS_FILTERS.map((status) => {
            const active = search.status === status;
            return (
              <button
                key={status ?? 'all'}
                type="button"
                aria-pressed={active}
                onClick={() => {
                  setFilter({ status });
                }}
                className={cn(
                  'rounded-full border-thick px-2.5 py-0.5 text-small font-semibold transition-colors',
                  active
                    ? 'border-strong bg-strong text-on-strong'
                    : 'border-border text-muted hover:text-text',
                )}
              >
                {status === undefined ? t('documents.filters.statusAll') : statusLabel(status)}
              </button>
            );
          })}
        </div>
        <DateRangeInput
          size="sm"
          aria-label={t('documents.filters.period')}
          className="w-56"
          value={search.from && search.to ? { from: search.from, to: search.to } : null}
          onValueChange={(range) => {
            setFilter({ from: range?.from, to: range?.to });
          }}
        />
        <TextInput
          aria-label={t('common.search')}
          placeholder={t('documents.searchPlaceholder')}
          leading={<Search className="size-4" aria-hidden="true" />}
          boxClassName="ml-auto w-full sm:w-64"
          value={typed}
          onValueChange={setTyped}
        />
      </div>

      {bindings.length > 0 ? (
        <div className="-mt-2 flex flex-wrap gap-1.5">
          {bindings.map((own) => (
            <span
              key={own.binding}
              className="inline-flex items-center gap-1 rounded-full bg-surface-2 py-0.5 pr-1 pl-2.5 text-small font-semibold"
            >
              {own.label}
              <button
                type="button"
                aria-label={t('documents.filters.removeBinding')}
                className="grid size-5 place-items-center rounded-full text-muted hover:bg-surface hover:text-text [&_svg]:size-3.5"
                onClick={() => {
                  setFilter({ [own.binding]: undefined });
                }}
              >
                <X />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      {list.isError ? (
        <Callout className="flex flex-wrap items-center gap-2">
          <span className="min-w-0 flex-1">{list.error.message}</span>
          <Button
            size="sm"
            onClick={() => {
              void list.refetch();
            }}
          >
            {t('common.retry')}
          </Button>
        </Callout>
      ) : (
        <>
          <DocumentTable
            items={items}
            loading={list.isPending}
            activeId={search.doc ?? null}
            onOpen={(doc) => {
              show(doc.id, search.doc !== undefined);
            }}
            empty={
              narrowed ? (
                <EmptyState
                  icon={<FileText />}
                  title={t('documents.emptyFilteredTitle')}
                  description={t('documents.emptyFilteredText')}
                  action={<Button onClick={resetFilters}>{t('documents.resetFilters')}</Button>}
                />
              ) : search.archived ? (
                <EmptyState
                  icon={<FileText />}
                  title={t('documents.emptyArchivedTitle')}
                  description={t('documents.emptyArchivedText')}
                />
              ) : (
                <EmptyState
                  icon={<FileText />}
                  title={t('documents.emptyTitle')}
                  description={canManage ? t('documents.emptyText') : t('documents.emptyReadOnlyText')}
                  action={uploadButton}
                />
              )
            }
          />
          {list.data && list.data.total > items.length ? (
            <p className="text-small text-muted">
              {t('documents.moreHint', { count: items.length, total: list.data.total })}
            </p>
          ) : null}
        </>
      )}

      {/* Постоянный key: переход к соседней записи не закрывает и не открывает панель заново. */}
      <AnimatePresence initial={false}>
        {search.doc ? (
          <DocumentPanel
            key="panel"
            id={search.doc}
            onOpenDocument={(id) => {
              show(id, false);
            }}
            onClose={() => {
              show(undefined, true);
            }}
            onPrevious={() => {
              const previous = items[openIndex - 1];
              if (previous) show(previous.id, true);
            }}
            onNext={() => {
              const next = items[openIndex + 1];
              if (next) show(next.id, true);
            }}
            onExpand={() => {
              void navigate({ to: '/documents/$id', params: { id: search.doc ?? '' } });
            }}
          />
        ) : null}
      </AnimatePresence>

      <Dialog
        open={search.upload === true && canManage}
        onOpenChange={(open) => {
          if (open) return;
          void navigate({ search: (prev) => ({ ...prev, upload: undefined }), replace: true });
        }}
      >
        {search.upload === true && canManage ? (
          <UploadDialog
            preset={preset}
            onDone={(saved) => {
              void navigate({ search: (prev) => ({ ...prev, upload: undefined, doc: saved.id }) });
            }}
          />
        ) : null}
      </Dialog>
    </div>
  );
}
